import {
  AnyRate_Read,
  Rate_Create_In_Company_Request,
  RateFlatCommission_Read,
  RateFlatRate_Read,
  RateOwnerType,
  RateType,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { isNil } from 'lodash-es';

import { AccountBasic } from '~hooks/useAccount/models';
import { AddOnBasic } from '~hooks/useAddOns/models';
import { CompanyBasic } from '~hooks/useCompany/models';
import {
  BasicEquipment,
  Equipment,
  EquipmentTypeBasic,
} from '~hooks/useEquipment/models';
import { ProjectBasic } from '~hooks/useProjects/models';
import { BasicServiceClass } from '~hooks/useServiceClass/models';
import { Service } from '~hooks/useServices/models';
import { RatesFormSchemaInterface } from '~pages/Settings/RatesManagement/rateFormValidationSchema';
import { rateTypeLabelColor } from '~pages/Settings/RatesManagement/rateUtils';
import { Nullable } from '~types/Nullable';
import isEnumValue from '~utils/isEnumValue';

export class ItemColorLabelValue {
  public static parse(proto: any): ItemColorLabelValue {
    return new ItemColorLabelValue(
      proto.color ?? '',
      proto.label ?? '',
      proto.value ?? '',
    );
  }
  public get color(): string {
    return this._color;
  }
  public get label(): string {
    return this._label;
  }
  public get value(): RateType {
    return this._value;
  }
  constructor(
    private _color: string,
    private _label: string,
    private _value: RateType,
  ) {}
}
const getIds = (items: ProjectBasic[] | Service[] | Equipment[]) => {
  return items?.length ? items.map((item) => item?.id) : undefined;
};

export class RateCommissionInputs {
  public static parse(proto: any): RateCommissionInputs {
    return new RateCommissionInputs(proto.commission_percentage ?? 0);
  }
  public static deparse(rate: RatesFormSchemaInterface): any {
    return {
      commission_percentage: Number(rate?.rateInputs?.rate),
    };
  }
  public get commissionPercentage(): number {
    return this._commission_percentage;
  }
  constructor(private _commission_percentage: number) {}
}
export class RatePerUnitInputs {
  public static parse(proto: any): RatePerUnitInputs {
    return new RatePerUnitInputs(
      proto.unit_rate ?? 0,
      proto.unit_minimum ?? 0,
      proto.unit_increment ?? 0,
    );
  }
  public static deparse(rate: RatesFormSchemaInterface): any {
    return {
      unit_rate: rate?.rateInputs?.rate,
      unit_minimum: rate.rateInputs?.minimum,
      unit_increment: 0,
    };
  }
  public get perUnitRate(): number {
    return this._per_unit_rate;
  }
  public get perUnitMinimum(): number {
    return this._per_unit_minimum;
  }
  public get perUnitIncrement(): number {
    return this._per_unit_increment;
  }
  constructor(
    private _per_unit_rate: number,
    private _per_unit_minimum: number,
    private _per_unit_increment: number, // Private _per_unit_unit_of_measure: number, // private _per_unit_round_to_next_increment: number,
  ) {}
}
export class RateTimeInputs {
  public static parse(proto: any): RateTimeInputs {
    return new RateTimeInputs(
      proto.time_rate ?? 0,
      proto.time_minimum_minutes ?? 0,
      proto?.time_increment_minutes ?? 0,
    );
  }
  public static deparse(rate: RatesFormSchemaInterface): any {
    return {
      time_rate: rate.rateInputs?.rate,
      time_minimum_minutes: rate.rateInputs?.minimum?.match(/\d+/)[0],
      time_increment_minutes: (rate?.rateInputs?.increment?.match(/\d+/) || [])[0] || 0,
    };
  }
  public get timeRate(): number {
    return this._time_rate;
  }
  public get timeMinimumMinutes(): number {
    return this._time_minimum_minutes;
  }
  public get timeIncrementMinutes(): number {
    return this._time_increment_minutes;
  }
  constructor(
    public _time_rate: number,
    public _time_minimum_minutes: number,
    public _time_increment_minutes: number,
  ) {}
}

export class RateFlatCommissionInputs {
  public static parse(proto: RateFlatCommission_Read): RateFlatCommissionInputs {
    return new RateFlatCommissionInputs(proto.commission_amount ?? 0);
  }

  public static deparse(rate: RatesFormSchemaInterface): any {
    return {
      commission_amount: Number(rate?.rateInputs?.rate),
    };
  }

  public get commissionAmount(): number {
    return this._commission_amount;
  }

  constructor(private _commission_amount: number) {}
}

export class RateFlatRateInputs {
  public static parse(proto: RateFlatRate_Read): RateFlatRateInputs {
    return new RateFlatRateInputs(proto.flat_amount ?? 0);
  }

  public static deparse(rate: RatesFormSchemaInterface) {
    return {
      flat_amount: Number(rate?.rateInputs?.rate),
    };
  }

  public get flatAmount(): number {
    return this._flat_amount;
  }

  constructor(private _flat_amount: number) {}
}

const rateInputsDeparserByRateType = {
  [RateType.RATE_COMMISSION]: RateCommissionInputs.deparse,
  [RateType.RATE_FLAT_COMMISSION]: RateFlatCommissionInputs.deparse,
  [RateType.RATE_FLAT_RATE]: RateFlatRateInputs.deparse,
  [RateType.RATE_PER_BUSHEL]: RatePerUnitInputs.deparse,
  [RateType.RATE_PER_DAY]: RateTimeInputs.deparse,
  [RateType.RATE_PER_EACH]: RatePerUnitInputs.deparse,
  [RateType.RATE_PER_HOUR]: RateTimeInputs.deparse,
  [RateType.RATE_PER_LOAD]: RatePerUnitInputs.deparse,
  [RateType.RATE_PER_POUND]: RatePerUnitInputs.deparse,
  [RateType.RATE_PER_TON]: RatePerUnitInputs.deparse,
  [RateType.RATE_PER_TONNE]: RatePerUnitInputs.deparse,
  [RateType.RATE_PER_YARD]: RatePerUnitInputs.deparse,
} satisfies Record<RateType, (rate: RatesFormSchemaInterface) => object>;

const deparseByRateType = (rate: RatesFormSchemaInterface) => {
  const rateType = rate.type.value;

  return isEnumValue(RateType, rateType)
    ? rateInputsDeparserByRateType[rateType](rate)
    : undefined;
};

const rateInputsParserByRateType = {
  [RateType.RATE_COMMISSION]: RateCommissionInputs.parse,
  [RateType.RATE_FLAT_COMMISSION]: RateFlatCommissionInputs.parse,
  [RateType.RATE_FLAT_RATE]: RateFlatRateInputs.parse,
  [RateType.RATE_PER_BUSHEL]: RatePerUnitInputs.parse,
  [RateType.RATE_PER_DAY]: RateTimeInputs.parse,
  [RateType.RATE_PER_EACH]: RatePerUnitInputs.parse,
  [RateType.RATE_PER_HOUR]: RateTimeInputs.parse,
  [RateType.RATE_PER_LOAD]: RatePerUnitInputs.parse,
  [RateType.RATE_PER_POUND]: RatePerUnitInputs.parse,
  [RateType.RATE_PER_TON]: RatePerUnitInputs.parse,
  [RateType.RATE_PER_TONNE]: RatePerUnitInputs.parse,
  [RateType.RATE_PER_YARD]: RatePerUnitInputs.parse,
} satisfies Record<
  RateType,
  (
    proto: any,
  ) =>
    | RatePerUnitInputs
    | RateTimeInputs
    | RateCommissionInputs
    | RateFlatCommissionInputs
    | RateFlatRateInputs
>;

// A type newer than this build's api spec parses as per-unit.
const getByRateType = (rateType: string, proto: any) =>
  isEnumValue(RateType, rateType)
    ? rateInputsParserByRateType[rateType](proto)
    : RatePerUnitInputs.parse(proto);
const getRateValueByType = (proto: any) => {
  if (!isNil(proto?.time_rate)) return proto.time_rate;
  if (!isNil(proto?.unit_rate)) return proto.unit_rate;
  if (!isNil(proto?.commission_percentage)) return proto.commission_percentage;
  if (!isNil(proto?.commission_amount)) return proto.commission_amount;
  if (!isNil(proto?.flat_amount)) return proto.flat_amount;
};

export class RateBasicWithValue {
  public static parse(proto: any): RateBasicWithValue {
    return new RateBasicWithValue(
      proto.id ?? '',
      proto.name ?? '',
      proto.type ?? '',
      getRateValueByType(proto),
      proto.add_ons ? proto.add_ons.map((addOn: any) => AddOnBasic.parse(addOn)) : [],
    );
  }
  public get readableTypeValue(): string {
    return rateTypeLabelColor(this._type as unknown as RateType)?.label || '';
  }
  public get type(): string {
    return this._type;
  }
  public get rate(): number {
    return this._rate;
  }
  public get id(): string {
    return this._id;
  }
  public get name(): string {
    return this._name;
  }
  public get addOns(): AddOnBasic[] {
    return this._add_ons;
  }
  constructor(
    private _id: string,
    private _name: string,
    private _type: string,
    private _rate: number,
    private _add_ons: AddOnBasic[] = [],
  ) {}
}
export class Rate {
  public static parse(proto: AnyRate_Read): Rate {
    return new Rate(
      proto.id ?? '',
      proto.name ?? '',
      proto.account ? AccountBasic.parse(proto.account) : null,
      proto.projects?.length ? proto.projects.map(ProjectBasic.parse) : [],
      proto.services?.length ? proto.services.map(Service.parse) : [],
      proto.equipment?.length ? proto.equipment.map(BasicEquipment.parse) : [],
      proto.primary ?? false,
      proto?.type?.length ? rateTypeLabelColor(proto.type as unknown as RateType) : null,
      proto.fuel_surcharge_enabled ?? false,
      proto.type && getByRateType(proto.type as unknown as RateType, proto),
      proto?.company?.id?.length ? CompanyBasic.parse(proto.company) : null,
      proto.owner_type,
      'time_rate' in proto ? (proto.time_rate ?? null) : null,
      proto.add_ons.map((addOn) => AddOnBasic.parse(addOn)),
      proto.equipment_type ? EquipmentTypeBasic.parse(proto.equipment_type) : undefined,
      proto.service_class ? BasicServiceClass.parse(proto.service_class) : undefined,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }
  public static deparseCreate(
    rate: RatesFormSchemaInterface,
    ownerType: RateOwnerType,
  ): Rate_Create_In_Company_Request {
    const deParsedInputs = deparseByRateType(rate);

    return {
      name: rate.name,
      account_id: rate.account?.id || undefined, // If non account, but company id, it will be a default company rate
      project_ids: getIds((rate.projects as ProjectBasic[]) || []),
      service_ids: getIds((rate.services as Service[]) || []),
      equipment_ids: getIds((rate.equipment as Equipment[]) || []),
      equipment_type_id: rate.equipmentType?.id || null,
      service_class_id: rate.serviceClass?.id || null,
      owner_type: ownerType,
      fuel_surcharge_enabled: rate.isFuelSurcharge,
      primary: rate.isPrimary,
      type: rate.type?.value,
      driver_id: rate.driver?.id || undefined,
      ...deParsedInputs,
    };
  }
  public static deparseUpdate(
    rate: RatesFormSchemaInterface,
    ownerType: RateOwnerType,
  ): any {
    const deParsedInputs = deparseByRateType(rate);
    return {
      name: rate.name,
      account_id: rate.account?.id || undefined,
      project_ids: getIds(rate.projects as ProjectBasic[]) || [],
      service_ids: getIds(rate.services as Service[]) || [],
      equipment_ids: getIds(rate.equipment as Equipment[]) || [],
      equipment_type_id: rate.equipmentType?.id || null,
      service_class_id: rate.serviceClass?.id || null,
      owner_type: ownerType,
      fuel_surcharge_enabled: rate.isFuelSurcharge,
      primary: rate.isPrimary,
      type: rate.type?.value,
      driver_id: rate.driver?.id || undefined,
      ...deParsedInputs,
    };
  }
  public get isTimeBased(): boolean {
    return (
      this._type?.value === RateType.RATE_PER_HOUR ||
      this._type?.value === RateType.RATE_PER_DAY
    );
  }
  public get isHourly(): boolean {
    return this._type?.value === RateType.RATE_PER_HOUR;
  }
  public get isDaily(): boolean {
    return this._type?.value === RateType.RATE_PER_DAY;
  }
  public get isCommission(): boolean {
    return this._type?.value === RateType.RATE_COMMISSION;
  }
  public get isFlatCommission(): boolean {
    return this._type?.value === RateType.RATE_FLAT_COMMISSION;
  }
  public get isFlatRate(): boolean {
    return this._type?.value === RateType.RATE_FLAT_RATE;
  }
  public get name(): string {
    return this._name;
  }
  public get id(): string | undefined {
    return this._id;
  }
  public get rateInputs():
    | RateTimeInputs
    | RatePerUnitInputs
    | RateCommissionInputs
    | RateFlatCommissionInputs
    | RateFlatRateInputs {
    return this._rate_inputs;
  }
  public get account(): Nullable<AccountBasic> {
    return this._account;
  }

  public get isPrimary(): boolean {
    return this._is_primary;
  }
  public get type(): ItemColorLabelValue {
    return <ItemColorLabelValue>this._type;
  }
  public get projects(): ProjectBasic[] {
    return this._projects;
  }
  public get services(): Service[] {
    return this._services;
  }
  public get equipment(): BasicEquipment[] {
    return this._equipment;
  }
  public get company(): Nullable<CompanyBasic> {
    return this._company;
  }
  public get isFuelSurcharge(): boolean {
    return this._is_fuel_surcharge;
  }
  public get ownerType() {
    return this._owner_type;
  }

  public get timeRate() {
    return this._time_rate;
  }

  public get addOns() {
    return this._add_ons ?? [];
  }

  public get equipmentType() {
    return this._equipment_type;
  }

  public get serviceClass() {
    return this._service_class;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  constructor(
    private _id: string,
    private _name: string,
    private _account: Nullable<AccountBasic>,
    private _projects: ProjectBasic[],
    private _services: Service[],
    private _equipment: BasicEquipment[],
    private _is_primary: boolean,
    private _type: Nullable<ItemColorLabelValue>,
    private _is_fuel_surcharge: boolean,
    private _rate_inputs:
      | RateCommissionInputs
      | RatePerUnitInputs
      | RateTimeInputs
      | RateFlatCommissionInputs
      | RateFlatRateInputs,
    private _company: Nullable<CompanyBasic>,
    private _owner_type: RateOwnerType,
    private _time_rate: number | null = null,
    private _add_ons?: AddOnBasic[],
    private _equipment_type?: EquipmentTypeBasic,
    private _service_class?: BasicServiceClass,
    private _discarded_at: Dayjs | null = null,
  ) {}
}
